import { Button } from "@/components/ui/button";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from "@/components/ui/dialog";
import { useNotifications } from "@/hooks/useNotifications";
import { useState } from "react";
import { useQueryClient } from "@tanstack/react-query";
import { Profile } from "@/hooks/profile/types";
import { PaymentFormContent } from "./form/PaymentFormContent";
import { CreditCard } from "lucide-react";
import { insertPaymentWithBeneficiaries } from "@/lib/payments/insertGroupPayment";

interface PaymentFormProps {
  user: Profile & { id: string };
  gymId: string | null;
  onPaymentRegistered: () => void;
  triggerClassName?: string;
}

export const PaymentForm = ({ user, gymId, onPaymentRegistered, triggerClassName }: PaymentFormProps) => {
  const [isOpen, setIsOpen] = useState(false);
  const { success, error: showError } = useNotifications();
  const queryClient = useQueryClient();

  const handleSubmit = async (data: {
    amount: number;
    discount: number;
    description: string;
    validity_days: number;
    paid_at: string;
    payment_method: string;
    covered_user_ids: string[];
  }) => {
    try {
      const payerName =
        [user.first_name, user.last_name].filter(Boolean).join(" ").trim() ||
        user.username?.split("@")[0] ||
        "otro socio";

      await insertPaymentWithBeneficiaries({
        payerId: user.id,
        amount: data.amount,
        discount: data.discount,
        description: data.description,
        validityDays: data.validity_days,
        paidAt: data.paid_at,
        paymentMethod: data.payment_method,
        coveredUserIds: data.covered_user_ids,
        payerDisplayName: payerName,
      });

      const extras = data.covered_user_ids?.length || 0;
      success("Pago registrado", {
        description:
          extras > 0
            ? `Pago grupal: ${extras + 1} socios quedaron al día`
            : "El pago ha sido registrado exitosamente",
      });

      await queryClient.invalidateQueries({ queryKey: ["payments"] });
      await queryClient.invalidateQueries({ queryKey: ["lastPayment"] });
      await queryClient.invalidateQueries({ queryKey: ["user-payments"] });
      await queryClient.invalidateQueries({ queryKey: ["arqueo-payments"] });
      await queryClient.invalidateQueries({ queryKey: ["arqueo-closure"] });

      setIsOpen(false);
      onPaymentRegistered();
    } catch (error) {
      console.error("Error al registrar el pago:", error);
      showError("No se pudo registrar el pago");
    }
  };

  return (
    <Dialog open={isOpen} onOpenChange={setIsOpen}>
      <DialogTrigger asChild>
        <Button variant="outline" size="icon" className={`sm:px-[15px] sm:w-auto ${triggerClassName ?? ""}`} title="Registrar pago" aria-label="Registrar pago">
          <CreditCard className="h-4 w-4" />
        </Button>
      </DialogTrigger>
        <DialogContent className="w-[95%] max-w-md mx-auto p-4 sm:p-6 max-h-[90vh] overflow-y-auto" onPointerDownOutside={(e) => { e.stopPropagation(); setIsOpen(false); }}>
        <DialogHeader>
          <DialogTitle className="text-lg sm:text-xl text-center sm:text-left">
            Registrar pago para {user.first_name || user.username?.split('@')[0]}
          </DialogTitle>
        </DialogHeader>
        <PaymentFormContent gymId={gymId} payerId={user.id} onSubmit={handleSubmit} onCancel={() => setIsOpen(false)} />
      </DialogContent>
    </Dialog>
  );
};
